import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Box, Button, CircularProgress, Grid, styled, Typography} from "@mui/material";
import {ErrorBoundary} from "react-error-boundary";
import {ErrorFallback} from "../../../../components/index";
import {SuittService} from "../../../../services";
import {DataGrid} from "@mui/x-data-grid";
import {Link} from "react-router-dom";
import {StoreContext} from "App";
import FileDownload from "js-file-download";
import {snackActions} from "../../../../utils/SnackBarUtils";


function UserScientificWorks({user = {}}) {
    const {auth} = useContext(StoreContext);
    const authUser = auth.user;
    const suittService = useMemo(() => new SuittService(), []);
    const columns = [
        {
            field: "author",
            headerName: "Автор",
            width: 150,
            valueGetter: () => `${user.fullNameUA.lastName} ${user.fullNameUA.firstName}`
        },
        {
            field: "name",
            headerName: "Назва",
            width: 150
        },
        {
            field: "type",
            headerName: "Характер роботи",
            width: 125
        },
        {
            field: "output",
            headerName: "Вихідні дані",
            width: 100
        },
        {
            field: "volumeOfPages",
            headerName: "Обсяг (в сторінках)",
            width: 150
        },
        {
            field: "coauthors",
            headerName: "Співавтори",
            width: 125
        },
        {
            field: "link",
            headerName: "Посилання",
            width: 150,
            renderCell: (params) => {
                /*      const onClick = (e) => {
                        e.stopPropagation();
                      };*/
                let link = params.row.link.indexOf("https") !== -1 ?
                    "https://" + params.row.link.split("https://")[1]
                    :
                    "http://" + params.row.link.split("http://")[1];

                if (params.row.link.indexOf("http") === -1 && params.row.link.indexOf("https") === -1) link = "-";

                return (
                    <a href={link} target={"_blank"} rel="noreferrer">{link}</a>
                );
            }
        },
        {
            field: "isScopus",
            headerName: "Scopus",
            width: 100,
            valueGetter: (params) => params.row.isScopus ? "Так" : "Ні"
        },
        {
            field: "isWoS",
            headerName: "WoS",
            width: 100,
            valueGetter: (params) => params.row.isWoS ? "Так" : "Ні"
        },
        {
            field: "isCopernicus",
            headerName: "Copernicus",
            flex: 1,
            valueGetter: (params) => params.row.isCopernicus ? "Так" : "Ні"
        },
        {
            field: "image",
            headerName: "Файл",
            width: 175,
            renderCell: (params) => {
                const image = params.row.image;
                if (!image) return '-';

                return (
                    <a href={`https://eforms.suitt.edu.ua/files/${image}`} target={"_blank"} rel="noreferrer">{image}</a>
                );
            }
        },
        {
            field: "group",
            headerName: "Група",
            width: 150
        }
    ];
    const [works, setWorks] = useState({
        data: [],
        loading: true
    });

    const updateUsers = useCallback(async () => {
        const result = await suittService.getUserWorks(user._id);
        setTimeout(() => {
            if (!result.status) return setWorks((prev) => ({...prev, loading: false}));

            const data = result.data.map((work) => ({...work, id: work._id}));
            setWorks((prev) => ({...prev, data, loading: false}));
        }, 500);
    }, [suittService, user._id]);

    useEffect(() => {
        (async () => updateUsers())();
    }, [updateUsers]);

    const StickyDataGrid = styled(DataGrid)(({theme}) => ({
        '& .MuiDataGrid-columnHeaders': {
            position: "sticky",
            // Replace background colour if necessary
            backgroundColor: theme.palette.background.paper,
            // Display header above grid data, but below any popups
            zIndex: theme.zIndex.mobileStepper - 1,
        },
        '& .MuiDataGrid-virtualScroller': {
            // Undo the margins that were added to push the rows below the previously fixed header
            marginTop: "0 !important"
        },
        '& .MuiDataGrid-main': {
            // Not sure why it is hidden by default, but it prevented the header from sticking
            overflow: "visible"
        }
    }));

    if (works.loading) {
        return (
            <Box sx={{display: "flex", justifyContent: "center", marginTop: 3}}>
                <CircularProgress color="secondary"/>
            </Box>
        );
    }

    if (
        works.data.length === 0
    ) return (
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center"}}>
            <Typography variant={"b"} component={"b"} sx={{fontWeight: 500}}>Нічого не знайдено</Typography>
            {
                authUser?._id === user._id && (
                    <Box sx={{display: "flex", justifyContent: "center", marginTop: 2}}>
                        <Button component={Link} to={`/cabinet/users/${user._id}/work/add`}
                                variant={"contained"}>Додати</Button>
                    </Box>
                )
            }
        </Box>
    );

    const downloadWorksDataXlsx = async () => {
        const filename = `user-works_${user.fullNameUA.lastName}_${user.fullNameUA.firstName}.xlsx`;
        const result = await suittService.downloadUserWorksDataXlsx({userId: user._id});
        FileDownload(result, filename);
        return snackActions.success("Звіт завантажується");
    };

    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
        >
            <Grid container sx={{padding: "10px 0"}}>
                <Grid sx={{textAlign: "left"}} xs={12} sm={12} md={12}>
                    {works.data.length > 0 && (
                        <Grid item>
                            <Typography variant={'h6'}>
                                {'Формування повного переліку наукових та науково-методичних праць науково-педагогічного працівника'}
                            </Typography>
                            <Typography
                                component={"b"}
                                variant={"b"}
                            >
                                {"Досягнення у професійній діяльності (за останні 5 років)"}
                            </Typography>
                            <Grid item xs={12} sm={12} md={12} lg={12}
                                  sx={{marginTop: 1, display: 'flex', justifyContent: 'flex-end', gap: 1}}>
                                {
                                    authUser?._id === user._id && (
                                        <Button component={Link} to={`/cabinet/users/${user._id}/work/add`}
                                                variant={"contained"}>Додати</Button>
                                    )
                                }
                                <Button variant={"contained"} onClick={downloadWorksDataXlsx}>ЕКСПОРТ ДАНИХ
                                    (XLSX)</Button>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12} lg={12} sx={{marginTop: 1}}>
                                <div style={{height: "50vh", width: "100%"}}>
                                    <StickyDataGrid
                                        rows={works.data}
                                        columns={columns}
                                        initialState={{
                                            pagination: {
                                                paginationModel: {
                                                    pageSize: 5,
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </ErrorBoundary>
    );
}

export default UserScientificWorks;
